import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@src/auth/AuthContext';
import {
    fetchAccount,
    fetchCompanies,
    fetchLocations
} from '@src/api/accountApi';
import type { AccountData } from '@src/api/accountApi';
import { fetchLDR } from '@src/api/ldrApi';
import { setLDR } from '@src/config/ldrCache';
import { fetchRoleMappings } from '@src/api/roleMappingApi';
import { setRoleMappings } from '@src/config/roleMappingCache';
import { createSalt, hashPunchId } from '@src/config/pinHash';
import { resetClient } from '@src/api/TimePunchClockApi';

// Fire-and-forget: refreshes the offline role/department cache (API-4378/
// API-4649) whenever the account resolves for a company/location — covers
// both app startup and manual location switches (`selectLocation`), since
// the cache is scoped per `${companyId}:${locationId}`. Never blocks the
// punch pad from showing and fails soft on error (LDR cache read already
// tolerates a miss).
//
// The external role ID mapping cache (API-4674) is hydrated in the same
// refresh so offline always has fresh-enough data. It needs `applicationName`
// to scope the integration; skipped on the browser OAuth path (no init
// payload, so no `applicationName`).
function refreshLDRCache(
    companyId: number,
    locationId: number,
    accessToken: string,
    applicationName?: string
): void {
    void fetchLDR(companyId, locationId, accessToken)
        .then(({ roles, departments }) =>
            setLDR(companyId, locationId, roles, departments)
        )
        .catch(err => {
            console.error('[AccountContext] Failed to refresh LDR cache', err);
        });

    if (!applicationName) return;

    void fetchRoleMappings(companyId, locationId, applicationName, accessToken)
        .then(mappings => setRoleMappings(companyId, locationId, mappings))
        .catch(err => {
            console.error(
                '[AccountContext] Failed to refresh role mapping cache',
                err
            );
        });
}

export interface AccountInitParams {
    companyId: number;
    /**
     * The integration application name from `7punches:init` (e.g. `'toast'`).
     * Required by the host-init (webview) path — `isInitPayload` rejects init
     * messages that omit it. Absent on the browser OAuth company-picker path
     * (`selectCompany`), which has no init payload to source it from.
     */
    applicationName?: string;
}

export interface TerminalAccount {
    userId: number;
    userName: string;
    companyId: number;
    companyName: string;
    logoUrl?: string;
    locationId: number;
    locationName: string;
    locationHash: string;
    locationTimezone: string;
    /**
     * The integration application name from init (see `AccountInitParams`).
     * Present on every mode (PIN and external-user) — required so that any
     * server-side integration-mapping lookup (e.g. punch enrichment,
     * API-4675/API-4676) can scope by integration. Undefined only on the
     * browser OAuth company-picker path, which has no init payload.
     */
    applicationName?: string;
}

export interface AvailableCompany {
    id: number;
    name: string;
    logoUrl?: string;
}

export interface AvailableLocation {
    id: number;
    name: string;
    hash: string;
    timezone: string;
}

export interface AccountContextValue {
    account: TerminalAccount | null;
    isLoading: boolean;
    error: string | null;

    availableCompanies: AvailableCompany[];
    isLoadingCompanies: boolean;
    needsCompanyPick: boolean;

    availableLocations: AvailableLocation[];
    isLoadingLocations: boolean;
    needsLocationPick: boolean;

    initParams: AccountInitParams | null;
    startInit: (params: AccountInitParams) => void;
    selectCompany: (companyId: number) => void;
    selectLocation: (locationId: number) => void;
    clearAccount: () => void;
    /**
     * Verifies an entered punch ID against the authenticated admin's punch ID
     * (API-4551). The punch ID itself is never exposed on the context — only a
     * salted digest is held (see `pinHash.ts`) and digests are compared here.
     * Fail-closed: resolves `false` when the account has no punch ID (or the
     * digest isn't ready yet).
     */
    verifyPunchId: (entered: string) => Promise<boolean>;
}

const AccountContext = createContext<AccountContextValue | null>(null);

function isAuthError(err: unknown): boolean {
    const msg = err instanceof Error ? err.message : String(err);
    return msg.includes('(401)');
}

export function AccountProvider({ children }: { children: ReactNode }) {
    const { accessToken, isAuthenticated, markSessionExpired } = useAuth();

    const [initParams, setInitParams] = useState<AccountInitParams | null>(
        null
    );
    const [account, setAccount] = useState<TerminalAccount | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [availableCompanies, setAvailableCompanies] = useState<
        AvailableCompany[]
    >([]);
    const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
    const [needsCompanyPick, setNeedsCompanyPick] = useState(false);

    const [availableLocations, setAvailableLocations] = useState<
        AvailableLocation[]
    >([]);
    const [isLoadingLocations, setIsLoadingLocations] = useState(false);
    const [needsLocationPick, setNeedsLocationPick] = useState(false);

    // Internal: set by selectLocation, triggers the account fetch effect.
    const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
        null
    );

    // Internal: caches fetchAccount() results (and in-flight requests) keyed by
    // companyId. Lets Effect 3 reuse Effect 1b's fetch (webview path) instead
    // of re-fetching the same data — and vice versa if Effect 3 wins the race.
    //
    // Invalidated below whenever accessToken changes (rather than folding the
    // token into the cache key itself) so a re-authenticated session — e.g.
    // after session expiry — never reuses account data fetched under a
    // previous token. Keying on the token directly would work too, but a
    // truncated/partial token would risk key collisions (JWTs from the same
    // issuer often share a header/payload prefix), and keying on the full
    // token means holding onto sensitive token material as Map keys longer
    // than necessary.
    const accountDataCache = useRef<Map<number, AccountData>>(new Map());
    const accountDataInFlight = useRef<Map<number, Promise<AccountData>>>(
        new Map()
    );
    const previousAccessTokenRef = useRef<string | null>(null);

    // Salted digest of the authed admin's punch ID (API-4551). Kept in refs —
    // never on the exposed context/state — so the value can't be read from
    // React DevTools or component props. The salt is generated once per session.
    const saltRef = useRef<string | null>(null);
    if (saltRef.current === null) {
        saltRef.current = createSalt();
    }
    const punchIdHashRef = useRef<string | null>(null);

    // Runs on every render (not in a useEffect) so the invalidation happens
    // before any effect below can populate the cache under the new token.
    if (accessToken !== previousAccessTokenRef.current) {
        accountDataCache.current.clear();
        accountDataInFlight.current.clear();
        // Fail closed across a session change: drop the previous user's punch-ID
        // digest immediately so verifyPunchId() can't accept their PIN during
        // the gap before the new account loads and re-hashes (API-4551).
        punchIdHashRef.current = null;
        previousAccessTokenRef.current = accessToken;
    }

    const getAccountData = useCallback(
        (companyId: number, token: string): Promise<AccountData> => {
            const cached = accountDataCache.current.get(companyId);
            if (cached) return Promise.resolve(cached);

            const inFlight = accountDataInFlight.current.get(companyId);
            if (inFlight) return inFlight;

            const promise = fetchAccount(companyId, token)
                .then(data => {
                    accountDataCache.current.set(companyId, data);
                    return data;
                })
                .finally(() => {
                    accountDataInFlight.current.delete(companyId);
                });
            accountDataInFlight.current.set(companyId, promise);
            return promise;
        },
        []
    );

    // Effect 1 — Fetch company list (browser OAuth path).
    // Skipped when initParams is already set (webview path bypasses company pick).
    useEffect(() => {
        if (!isAuthenticated || !accessToken || initParams) return;

        const token = accessToken;
        let cancelled = false;
        setIsLoadingCompanies(true);
        setError(null);

        async function load() {
            try {
                const companies = await fetchCompanies(token);
                if (cancelled) return;
                setAvailableCompanies(companies);
                if (companies.length === 0) {
                    setError('account.noCompanies');
                } else if (companies.length === 1) {
                    setInitParams({ companyId: companies[0].id });
                } else {
                    setNeedsCompanyPick(true);
                }
            } catch (err) {
                if (!cancelled) {
                    if (isAuthError(err)) {
                        markSessionExpired();
                    } else {
                        console.error(err);
                        setError('account.failedToLoadCompanies');
                    }
                }
            } finally {
                if (!cancelled) setIsLoadingCompanies(false);
            }
        }

        void load();
        return () => {
            cancelled = true;
            setIsLoadingCompanies(false);
        };
    }, [isAuthenticated, accessToken, initParams, markSessionExpired]);

    // Effect 1b — Resolve the single company's display name for the
    // webview/host-init path (API-4672). Effect 1 (fetchCompanies) is skipped
    // whenever initParams arrives already set — e.g. via startInit() from the
    // `7punches:init` postMessage — so availableCompanies stays empty and
    // SelectionPage's disabled company field falls back to its placeholder
    // text. Fetch just this company's name via fetchAccount() and populate
    // availableCompanies with a single entry so the field displays it. Not
    // needed on the browser OAuth path, since Effect 1 already populates
    // availableCompanies before initParams is ever set there.
    //
    // Guarded on the *current* companyId being present in availableCompanies
    // (not merely a nonempty list) so a repeated host init for a different
    // company — e.g. a second `7punches:init` postMessage — re-resolves the
    // name instead of leaving the dropdown on the previous company or a
    // stale placeholder.
    useEffect(() => {
        if (!isAuthenticated || !accessToken || !initParams) return;
        const { companyId } = initParams;
        if (availableCompanies.some(c => c.id === companyId)) return;

        const token = accessToken;
        let cancelled = false;

        async function load() {
            try {
                const data = await getAccountData(companyId, token);
                if (cancelled) return;
                setAvailableCompanies([
                    { id: data.companyId, name: data.companyName }
                ]);
            } catch (err) {
                if (!cancelled) {
                    if (isAuthError(err)) {
                        markSessionExpired();
                    } else {
                        // Display-only convenience — fail soft and let the
                        // rest of the init flow (locations, account) proceed
                        // normally even if the name never resolves.
                        console.error(
                            '[AccountContext] Failed to resolve company name',
                            err
                        );
                    }
                }
            }
        }

        void load();
        return () => {
            cancelled = true;
        };
    }, [
        isAuthenticated,
        accessToken,
        initParams,
        availableCompanies,
        getAccountData,
        markSessionExpired
    ]);

    // Effect 2 — Fetch locations for the selected company.
    // Fires when initParams is set, whether from the webview path, auto-selection,
    // or a user picking a company.
    useEffect(() => {
        if (!isAuthenticated || !accessToken || !initParams) return;

        const token = accessToken;
        const { companyId } = initParams;
        let cancelled = false;
        setIsLoadingLocations(true);
        setNeedsCompanyPick(false);
        setSelectedLocationId(null);
        setError(null);

        async function load() {
            try {
                const locations = await fetchLocations(companyId, token);
                if (cancelled) return;
                setAvailableLocations(locations);
                if (locations.length === 0) {
                    setError('account.noLocations');
                } else if (locations.length === 1) {
                    setSelectedLocationId(locations[0].id);
                } else {
                    setNeedsLocationPick(true);
                }
            } catch (err) {
                if (!cancelled) {
                    if (isAuthError(err)) {
                        markSessionExpired();
                    } else {
                        console.error(err);
                        setError('account.failedToLoadLocations');
                    }
                }
            } finally {
                if (!cancelled) setIsLoadingLocations(false);
            }
        }

        void load();
        return () => {
            cancelled = true;
            setIsLoadingLocations(false);
        };
    }, [isAuthenticated, accessToken, initParams, markSessionExpired]);

    // Effect 3 — Fetch account (user data) once a location is confirmed.
    useEffect(() => {
        if (
            !isAuthenticated ||
            !accessToken ||
            !initParams ||
            !selectedLocationId
        )
            return;

        const token = accessToken;
        const locationId = selectedLocationId;
        const { companyId, applicationName } = initParams;
        let cancelled = false;
        setIsLoading(true);
        setNeedsLocationPick(false);
        setError(null);

        async function load() {
            try {
                // Reuse Effect 1b's in-flight/resolved fetchAccount() call when
                // available (webview path) instead of issuing a duplicate
                // request, regardless of which effect wins the race.
                const data = await getAccountData(companyId, token);
                if (cancelled) return;
                const loc = availableLocations.find(l => l.id === locationId);
                const company = availableCompanies.find(
                    c => c.id === data.companyId
                );
                setAccount({
                    userId: data.userId,
                    userName: data.userName,
                    companyId: data.companyId,
                    companyName: data.companyName,
                    logoUrl: company?.logoUrl,
                    locationId,
                    locationName: loc?.name ?? '',
                    locationHash: loc?.hash ?? data.fallbackLocationHash,
                    locationTimezone: loc?.timezone ?? 'UTC',
                    applicationName
                });
                // Hash the punch ID and immediately drop the plaintext — only
                // the digest is retained (in a ref), never exposed on state.
                const rawPunchId = data.punchId;
                if (rawPunchId) {
                    void hashPunchId(saltRef.current!, rawPunchId)
                        .then(hash => {
                            if (!cancelled) punchIdHashRef.current = hash;
                        })
                        .catch(() => {
                            punchIdHashRef.current = null;
                        });
                } else {
                    punchIdHashRef.current = null;
                }
                refreshLDRCache(data.companyId, locationId, token, applicationName);
            } catch (err) {
                if (!cancelled) {
                    if (isAuthError(err)) {
                        markSessionExpired();
                    } else {
                        console.error(err);
                        setError('account.failedToLoadAccount');
                    }
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }

        void load();
        return () => {
            cancelled = true;
        };
    }, [
        isAuthenticated,
        accessToken,
        initParams,
        selectedLocationId,
        markSessionExpired,
        availableLocations,
        availableCompanies,
        getAccountData
    ]);

    const startInit = useCallback((params: AccountInitParams) => {
        setInitParams(params);
    }, []);

    const selectCompany = useCallback((companyId: number) => {
        setAvailableLocations([]);
        setSelectedLocationId(null);
        // Invalidate the cached punch-ID digest — the new company resolves a
        // different account whose punch ID is re-hashed by Effect 3 (API-4551).
        punchIdHashRef.current = null;
        setInitParams({ companyId });
    }, []);

    const selectLocation = useCallback((locationId: number) => {
        // Switching location rebuilds the KMP punch client in place — tear
        // down the existing client first (API-4681) so the next punch
        // rebuilds against the new location's Koin graph.
        resetClient();
        // Invalidate the cached punch-ID digest — it's recomputed by Effect 3
        // once the new location's account resolves.
        punchIdHashRef.current = null;
        // Clear the account immediately (rather than waiting for Effect 3's
        // async re-fetch to overwrite it) so the punch pad unmounts for the
        // duration of the switch. Without this, a punch submitted while the
        // old account is still in state would call getOrCreateClient() with
        // the old location's params — recreating the singleton against the
        // *old* location — and since the singleton is then non-null, it
        // would survive even after the new account loads, silently punching
        // against the wrong location (API-4681).
        setAccount(null);
        setNeedsLocationPick(false);
        setSelectedLocationId(locationId);
    }, []);

    const clearAccount = useCallback(() => {
        accountDataCache.current.clear();
        accountDataInFlight.current.clear();
        punchIdHashRef.current = null;
        setInitParams(null);
        setAccount(null);
        setIsLoading(false);
        setError(null);
        setAvailableCompanies([]);
        setIsLoadingCompanies(false);
        setNeedsCompanyPick(false);
        setAvailableLocations([]);
        setIsLoadingLocations(false);
        setNeedsLocationPick(false);
        setSelectedLocationId(null);
    }, []);

    const verifyPunchId = useCallback(
        async (entered: string): Promise<boolean> => {
            const hash = punchIdHashRef.current;
            // Fail-closed: no digest → no punch ID configured (or not yet
            // hashed) → nothing can match.
            if (!hash || !saltRef.current) return false;
            try {
                const enteredHash = await hashPunchId(saltRef.current, entered);
                return enteredHash === hash;
            } catch {
                return false;
            }
        },
        []
    );

    const value = useMemo<AccountContextValue>(
        () => ({
            account,
            isLoading,
            error,
            availableCompanies,
            isLoadingCompanies,
            needsCompanyPick,
            availableLocations,
            isLoadingLocations,
            needsLocationPick,
            initParams,
            startInit,
            selectCompany,
            selectLocation,
            clearAccount,
            verifyPunchId
        }),
        [
            account,
            isLoading,
            error,
            availableCompanies,
            isLoadingCompanies,
            needsCompanyPick,
            availableLocations,
            isLoadingLocations,
            needsLocationPick,
            initParams,
            startInit,
            selectCompany,
            selectLocation,
            clearAccount,
            verifyPunchId
        ]
    );

    return (
        <AccountContext.Provider value={value}>
            {children}
        </AccountContext.Provider>
    );
}

export function useTerminalAccount(): AccountContextValue {
    const ctx = useContext(AccountContext);
    if (!ctx) {
        throw new Error(
            'useTerminalAccount must be used within an AccountProvider'
        );
    }
    return ctx;
}
